// A bucket of like-priced units used by discount reservation. Modeling
// units this way (instead of an index-per-unit array) lets a fractional
// line quantity like 1.5 flow through discount math without triggering
// `Array(1.5)` RangeErrors and without forcing the caller to inflate the
// unit count.
export type PriceBucket = { quantity: number; remainingPrice: number };

// Fold adjacent buckets that ended up at the same per-unit remaining price
// so the working set stays small across many discount iterations.
export function mergeSamePriceBuckets(buckets: PriceBucket[]): PriceBucket[] {
  const merged: PriceBucket[] = [];

  for (const bucket of buckets) {
    if (bucket.quantity <= 0) continue;
    const existing = merged.find(
      (b) => Math.abs(b.remainingPrice - bucket.remainingPrice) < 0.000001,
    );
    if (existing) {
      existing.quantity += bucket.quantity;
    } else {
      merged.push({ ...bucket });
    }
  }

  return merged;
}

/**
 * What a prepaid use with an extra charge takes off one unit's running price.
 * It covers the same share of the price wherever it sits in the stack, so a
 * promotion before or after it lowers the member's top-up alike.
 */
export function topUpUnitDiscount(
  remainingPrice: number,
  unitPrice: number,
  additionalCharge: number,
): number {
  if (unitPrice <= 0) return 0;
  return remainingPrice * Math.max(0, 1 - additionalCharge / unitPrice);
}

/**
 * Apply one discount to the units with the most left first. The checkout total
 * and the per-discount amounts sent to the server both go through here, so the
 * two can't drift apart.
 */
export function applyDiscountToBuckets(
  buckets: PriceBucket[],
  discount: {
    amount?: number;
    percentage?: number;
    additionalCharge?: number;
    quantity: number;
  },
  unitPrice: number,
  itemQuantity: number,
): { buckets: PriceBucket[]; discount: number; appliedQuantity: number } {
  let quantityToApply = Math.min(discount.quantity, itemQuantity);
  let total = 0;
  let appliedTotal = 0;
  const sortedBuckets = [...buckets].sort(
    (a, b) => b.remainingPrice - a.remainingPrice,
  );
  const nextBuckets: PriceBucket[] = [];

  for (const bucket of sortedBuckets) {
    if (quantityToApply <= 0 || bucket.remainingPrice <= 0) {
      nextBuckets.push(bucket);
      continue;
    }

    const appliedQuantity = Math.min(bucket.quantity, quantityToApply);
    const untouchedQuantity = bucket.quantity - appliedQuantity;

    let perUnitDiscount = 0;
    if (discount.additionalCharge) {
      perUnitDiscount = topUpUnitDiscount(
        bucket.remainingPrice,
        unitPrice,
        discount.additionalCharge,
      );
    } else if (discount.percentage && discount.percentage > 0) {
      perUnitDiscount = (bucket.remainingPrice * discount.percentage) / 100;
    } else if (discount.amount && discount.amount > 0) {
      perUnitDiscount = Math.min(discount.amount, bucket.remainingPrice);
    }

    total += perUnitDiscount * appliedQuantity;
    appliedTotal += appliedQuantity;
    nextBuckets.push({
      quantity: appliedQuantity,
      remainingPrice: Math.max(0, bucket.remainingPrice - perUnitDiscount),
    });
    if (untouchedQuantity > 0) {
      nextBuckets.push({
        quantity: untouchedQuantity,
        remainingPrice: bucket.remainingPrice,
      });
    }
    quantityToApply -= appliedQuantity;
  }

  return {
    buckets: mergeSamePriceBuckets(nextBuckets),
    discount: total,
    appliedQuantity: appliedTotal,
  };
}

/**
 * The order to apply a line's discounts in: top-up benefits first, the rest as
 * listed. A top-up then always records the credit's full share and a promotion
 * only its share of what's left, so recognition reports the same split
 * whichever was added first.
 */
export function topUpsFirst(
  discounts: { additionalCharge?: number }[],
): number[] {
  const indices = discounts.map((_discount, index) => index);
  return [
    ...indices.filter((index) => discounts[index].additionalCharge),
    ...indices.filter((index) => !discounts[index].additionalCharge),
  ];
}
